import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import ExistingParametersDataGrid from "../../Components/Parameters/ExistingParametersDataGrid";
import { useDispatch, useSelector } from "react-redux";
import {
	getFields,
	getFieldsArray,
	getFieldsStatus,
	deleteProductField,
} from "../../redux/ProductsSlice";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import NewParameterDialog from "../../Components/Dialogs/NewParameterDialog";
import { useSnackbar } from "notistack";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";
import DeleteConfirmationDialog from "../../Components/Dialogs/DeleteConfirmationDialog";

const ManageParameters = () => {
	useEffect(() => {
		document.title = "Manage Parameters - CPC";
	}, []);

	const dispatch = useDispatch();
	const existingFields = useSelector(getFieldsArray);
	const existingFieldsStatus = useSelector(getFieldsStatus);

	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));
	const { enqueueSnackbar } = useSnackbar();

	const [chosenExistingFields, setChosenExistingFields] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [rows, setRows] = useState([]);
	const [rowToEdit, setRowToEdit] = useState(null);
	const [rowModesModel, setRowModesModel] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (existingFieldsStatus === "idle") {
			dispatch(getFields());
		}
	}, [dispatch, existingFieldsStatus]);

	useEffect(() => {
		if (existingFields && existingFields.length > 0) {
			setRows(existingFields);
		}
	}, [existingFields]);

	const columns = [
		{
			field: "name",
			headerName: "Name",
			width: isXs ? 160 : 230,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "displayName",
			headerName: "Display Name",
			width: isXs ? 180 : 250,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "type",
			headerName: "Type",
			width: isXs ? 120 : 150,
			headerClassName: "new-param-theme--header",
			renderCell: (params) => {
				if (params.value === 3 || params.value === "Boolean") {
					return "Toggle"
				} else if (params.value === 4 || params.value === "Enumeration") {
					return "Single Selector"
				}
			}
		},
		{
			field: "uom",
			headerName: "Unit of Measure",
			width: isXs ? 100 : 120,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "enumOptions",
			headerName: "Options",
			width: isXs ? 120 : 150,
			headerClassName: "new-param-theme--header",
			renderCell: (params) => {
				if (
					Array.isArray(params.value) &&
					typeof params.value[0] === "object"
				) {
					return params.value?.length > 0
						? params.value.map((option) => option.name).join(", ")
						: "N/A";
				}

				return params.value?.length > 0 ? params.value.join(", ") : "N/A";
			},
		},
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			headerClassName: "new-param-theme--header",
			width: 100,
			cellClassName: "actions",
			getActions: (params) => {
				return [
					<GridActionsCellItem
						icon={<EditIcon color='secondary' />}
						label='Edit'
						className='textPrimary'
						onClick={handleEditClick(params.id)}
						color='inherit'
					/>,
					<DeleteConfirmationDialog
						header="Delete Confirmation"
						title="Are you sure you want to delete this parameter?"
						description="This will remove it from any product items and their templates."
						response={() => handleDeleteClick(params.id)}
					>
						{(showDialog) => (
							<GridActionsCellItem
								icon={<DeleteIcon color='error' />}
								label='Delete'
								onClick={showDialog}
								color='inherit'
							/>
						)}
					</DeleteConfirmationDialog>,
				];
			},
		},
	];

	const handleClose = () => {
		setOpenDialog(false);
		setRowToEdit(null);
	};

	const handleEditClick = (id) => () => {
		setRowToEdit(rows.find((row) => row.id === id));
		setOpenDialog(true);
	};

	const handleDeleteClick = (id) => {
		setIsSubmitting(true);
		let chosenFieldIds = [id];
		if (!id) {
			chosenFieldIds = chosenExistingFields;
		}

		dispatch(
			deleteProductField(
				TrimObjectStrings({
					payload: {
						ids: [...chosenFieldIds],
					},
				})
			)
		)
			.unwrap()
			.then(() => {
				setIsSubmitting(false);

				enqueueSnackbar("Parameter successfully deleted!", {
					variant: "success",
					autoHideDuration: 3000,
				});
			})
			.catch((error) => {
				let errorMessage = "Failed to delete parameter. Please try again.";

				console.error("Failed to delete parameter", error);
				setIsSubmitting(false);
				enqueueSnackbar(errorMessage, {
					variant: "error",
					autoHideDuration: 3000,
				});
			});

		setRowToEdit(null);
	};

	const handleRowModesModelChange = (newRowModesModel) => {
		setRowModesModel(newRowModesModel);
	};

	return (
		<div>
			{isSubmitting && <LoadingOverlay />}
			{rowToEdit && 
				<NewParameterDialog
					openDialog={openDialog}
					handleClose={handleClose}
					rows={rows}
					setRowModesModel={setRowModesModel}
					initialState={rowToEdit}
					setRowToEdit={setRowToEdit}
					existingFields={existingFields}
				/>}

			{existingFields ? (
				<Grid
					container
					spacing={3}
					justifyContent='space-between'
				>
					<Grid size={{ xs: 12 }}>
						<Typography variant='h2'>Manage Parameters</Typography>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<ExistingParametersDataGrid
							fields={existingFields}
							chosenFields={chosenExistingFields}
							setField={setChosenExistingFields}
							columns={columns}
							loading={existingFieldsStatus === "loading"}
							isAdmin
							rowModesModel={rowModesModel}
							handleRowModesModelChange={handleRowModesModelChange}
							setRows={setRows}
							setRowModesModel={setRowModesModel}
							handleDeleteItems={handleDeleteClick}
						/>
					</Grid>
				</Grid>
			) : null}
		</div>
	);
};

export default ManageParameters;
